import React, { useId } from "react";
import { useEditableForm } from "@/stores/useEditableFormStore";
import { AIGeneratedBadge } from "./AIGeneratedBadge";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: React.Ref<HTMLTextAreaElement>;
  label?: string;
  error?: string;
  helperText?: string;
  isAIInferred?: boolean;
  aiInferredReason?: string;
  /** Horizontal alignment of the textarea text. Defaults to "left". */
  textAlign?: "left" | "center" | "right";
}

const textAlignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

export function Textarea({
  ref,
  label,
  error,
  helperText,
  className = "",
  id,
  name,
  required,
  disabled,
  value,
  defaultValue,
  isAIInferred,
  aiInferredReason,
  textAlign = "left",
  ...props
}: TextareaProps) {
  const _id = useId();
  const fieldId = id || name || `textarea-${_id}`;
  const { isEditing, editableMode } = useEditableForm();
  const displayValue = value ?? defaultValue;

  if (editableMode && !isEditing) {
    return (
      <div className="w-full">
        {label && (
          <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="inline-flex justify-between mb-2 w-full">
            {label}
            {isAIInferred && <AIGeneratedBadge reason={aiInferredReason} />}
          </Typography>
        )}
        <div className={cn("px-4 py-3 text-gray-900 bg-gray-50 rounded-xl border border-gray-200 min-h-[120px] whitespace-pre-wrap", textAlignClass[textAlign])}>
          {displayValue || "—"}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {label && (
        <Typography as="label" htmlFor={fieldId} variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      <textarea
        ref={ref}
        id={fieldId}
        name={name}
        required={required}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        className={cn(
          "w-full px-4 py-3 rounded-xl border transition-all duration-200",
          "resize-y min-h-[120px]",
          textAlignClass[textAlign],
          error
            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
            : "border-gray-200 focus:border-navigator-primary focus:ring-navigator-primary/20",
          "focus:outline-none focus:ring-2",
          "bg-white text-navigator-text-primary",
          "placeholder:text-gray-400",
          "disabled:bg-gray-50 disabled:cursor-not-allowed",
          className
        )}
        {...props}
      />

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
      {helperText && !error && (
        <Typography as="p" variant="body" color="light" className="mt-1.5">{helperText}</Typography>
      )}
    </div>
  );
}
